import { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";
import { FaPencilAlt, FaCheck, FaTimes } from "react-icons/fa";
import { ShieldAlert } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import { useUser } from "../context/UserContext";
import { Loading } from "../Components/Loading/Loading";
import { AddUserModal } from "../Components/UserManagement/AddUserModal";
import { EditUserModal } from "../Components/UserManagement/EditUserModal";
import { SuccessModal } from "../Components/UserManagement/SuccessModal";

// Role flags shown/editable in the table (embedded user.role).
const ROLE_COLUMNS = [
  { key: "platform_admin", label: "Platform Admin" },
  { key: "poc", label: "JBM POC" },
  { key: "customer_support", label: "JBM CSM" },
  { key: "rcs_poc", label: "RCS POC" },
  { key: "rcs_csm", label: "RCS CSM" },
  { key: "pmo", label: "PMO" },
];

export const UserManagement = () => {
  const { user } = useUser();
  const authToken = localStorage.getItem("authToken");
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` };

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [usertypes, setUsertypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loaded, setLoaded] = useState(false);

  const isAdmin = user.loaded ? !!user.role?.platform_admin : null;

  const loadUsers = () => {
    fetch(`${BASE}/API/V2/users`, { headers })
      .then((r) => {
        if (r.status === 401) { redirectToLogin(); return null; }
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setUsers(data);
      });
  };

  useEffect(() => {
    if (!authToken) { redirectToLogin(); return; }
    if (!user.loaded) return;
    if (!user.role?.platform_admin) { setLoaded(true); return; }

    loadUsers();
    const getList = (url, set) =>
      fetch(`${BASE}${url}`, { headers })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => set(Array.isArray(d) ? d : []))
        .catch(() => {});
    Promise.all([
      getList("/API/V2/users/teams", setTeams),
      getList("/API/V2/users/usertypes", setUsertypes),
    ]).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loaded]);

  const getTeamName = (value) => teams.find((t) => t.value === value)?.key || value || "—";
  const getUserTypeName = (value) => usertypes.find((u) => u.value === value)?.key || value || "—";

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) { setFilteredUsers(users); return; }
    const match = (u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      getTeamName(u.team).toLowerCase().includes(q) ||
      getUserTypeName(u.usertype).toLowerCase().includes(q);
    setFilteredUsers(users.filter(match));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, users, teams, usertypes]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const renderBool = (value) =>
    value ? (
      <FaCheck className="text-green-600 w-4 h-4" />
    ) : (
      <FaTimes className="text-red-500 w-4 h-4" />
    );

  // RCS clients and partners are external and hold no internal roles — show a
  // neutral dash rather than a red cross for them.
  const renderRoleCell = (u, key) =>
    u.clientType === "RCS" || u.clientType === "partner" ? (
      <span className="text-gray-300">—</span>
    ) : (
      renderBool(u.role?.[key])
    );

  if (user.loaded && isAdmin === false) {
    return (
      <div className="p-10 flex flex-col items-center text-center text-gray-500 gap-3">
        <ShieldAlert size={40} className="text-jiored1" />
        <p className="font-semibold text-gray-700">Admins only</p>
        <p className="text-sm">You need the platform admin role to manage users.</p>
      </div>
    );
  }

  if (!loaded) return <Loading />;

  return (
    <div className="flex flex-col gap-2 p-2 pt-4 mx-3">
      <div className="flex gap-1 m-3 mb-0 mr-2 items-center justify-between">
        <div className="mr-2 flex items-center bg-white w-1/3 h-fit rounded-full border py-0.5">
          <span className="w-6 h-6 fill-gray-500 my-1.5 ml-3 mr-1 pt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
            </svg>
          </span>
          <input
            className="w-full px-1 text-gray-700 leading-tight tracking-tight focus:outline-none"
            type="text"
            placeholder="Search User"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className="flex items-center gap-2 bg-jiojbmpurple px-4 font-black py-2 text-white rounded-full transition"
          onClick={() => setIsAddModalOpen(true)}
        >
          <span className="flex items-center justify-center w-5 h-5 bg-white rounded-full">
            <GoPlus className="text-jiojbmpurple text-xl" />
          </span>
          <p className="tracking-tight">Onboard User</p>
        </button>
      </div>

      <div className="overflow-auto border border-gray-300 rounded-lg m-2">
        <table className="min-w-full bg-white rounded-lg shadow-sm" style={{ fontSize: "12px" }}>
          <thead>
            <tr>
              <th className="py-1.5 px-2 border-b text-left font-bold">Email</th>
              <th className="py-1.5 px-2 border-b text-left font-bold">Name</th>
              <th className="py-1.5 px-2 border-b text-left font-bold">Type</th>
              <th className="py-1.5 px-2 border-b text-left font-bold">Team</th>
              <th className="py-1.5 px-2 border-b text-left font-bold">User Type</th>
              {ROLE_COLUMNS.map((r) => (
                <th key={r.key} className="py-1.5 px-2 border-b text-left font-bold whitespace-nowrap">
                  {r.label}
                </th>
              ))}
              <th className="py-1.5 px-2 border-b text-left font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((u) => (
              <tr key={u.email} className="hover:bg-gray-50">
                <td className="py-1.5 px-2 border-b">{u.email}</td>
                <td className="py-1.5 px-2 border-b">{u.name}</td>
                <td className="py-1.5 px-2 border-b">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      u.clientType === "RCS"
                        ? "bg-jiojbm20 text-jiojbm50"
                        : u.clientType === "partner"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {u.clientType === "RCS"
                      ? "RCS Client"
                      : u.clientType === "partner"
                      ? "Partner"
                      : "Internal"}
                  </span>
                </td>
                <td className="py-1.5 px-2 border-b">{getTeamName(u.team)}</td>
                <td className="py-1.5 px-2 border-b">{getUserTypeName(u.usertype)}</td>
                {ROLE_COLUMNS.map((r) => (
                  <td key={r.key} className="py-1.5 px-2 border-b">
                    {renderRoleCell(u, r.key)}
                  </td>
                ))}
                <td className="py-1.5 px-2 border-b">
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-full transition"
                    onClick={() => { setUserToEdit(u); setIsEditModalOpen(true); }}
                    title="Edit user"
                  >
                    <FaPencilAlt className="w-4 h-4 text-jiojbmpurple" />
                  </button>
                </td>
              </tr>
            ))}
            {currentUsers.length === 0 && (
              <tr>
                <td colSpan={5 + ROLE_COLUMNS.length + 1} className="py-6 text-center text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center w-full px-2">
        <div className="flex-1 flex gap-16 pl-32 justify-center items-center">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-full ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
          >
            ‹
          </button>
          <p className="text-gray-700">Page {currentPage} of {totalPages}</p>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-full ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
          >
            ›
          </button>
        </div>
        <div className="ml-auto flex gap-x-2 items-center">
          <p className="font-bold text-jiojbmpurple tracking-tight text-sm whitespace-nowrap">Users Per Page</p>
          <select
            className="border border-gray-300 rounded-full px-3 py-1"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {isAddModalOpen && (
        <AddUserModal
          setIsAddModalOpen={setIsAddModalOpen}
          onSaved={loadUsers}
          onSuccess={setSuccessMessage}
          teams={teams}
          usertypes={usertypes}
          roleColumns={ROLE_COLUMNS}
        />
      )}
      {isEditModalOpen && (
        <EditUserModal
          setIsEditModalOpen={setIsEditModalOpen}
          userToEdit={userToEdit}
          onSaved={loadUsers}
          onSuccess={setSuccessMessage}
          teams={teams}
          usertypes={usertypes}
          roleColumns={ROLE_COLUMNS}
        />
      )}
      {successMessage && (
        <SuccessModal message={successMessage} onClose={() => setSuccessMessage("")} />
      )}
    </div>
  );
};
